import { FC } from "react";
import { useTrans } from "../../../hooks";
import { Text, HStack, Button, SimpleGrid, Box } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import { ICellTextProps, IDataRowProps } from "ka-table/props";
import {
    AppIcon,
    AppOptionBoxItem,
    AppOptionsBox,
    KaPageSizeSelector,
} from "../../../components";
import { useAuthData } from "../../../contexts";

export const TrainingList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    loadingList?: boolean;
    onActionClick?: (key: string, data?: any) => void;
}> = ({
    table,
    paging,
    list,
    loadingList = false,
    onActionClick = () => {},
}) => {
    const { t } = useTrans();
    const { grant } = useAuthData();
    const columns: Column[] = [
        {
            key: "show-hide-details-row",
            width: 50,
        },
        {
            key: "name",
            title: t("ent.Training:name.label"),
            dataType: DataType.String,
        },
        {
            key: "action",
            title: "",
            style: { textAlign: "right" },
            dataType: DataType.Object,
            width: 50,
            isSortable: false,
        },
    ];

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "edit",
            label: t("cmn:option.edit"),
            iconName: "icl-pencil",
            isDisabled: grant.isCompanyDirector,
        },
        {
            key: "delete",
            label: t("cmn:option.delete"),
            iconName: "icl-trash",
            isDisabled: grant.isCompanyDirector,
        },
    ];

    const DetailsRow: React.FC<IDataRowProps> = ({ rowData }: any) => {
        return (
            <>
                <Text textStyle={"mediumLg"} ps={2} pb={2}>
                    {t("padm.TrainingPage:text.detailRow.trainingYears")}
                </Text>
                <SimpleGrid
                    columns={{ sm: 2, md: 3, lg: 4 }}
                    spacingX={10}
                    spacingY={5}
                    p={2}
                >
                    {rowData.trainingYears.map((item) => (
                        <Text
                            key={item.id}
                            whiteSpace={"wrap"}
                            textStyle={"regularSm"}
                        >
                            <Box
                                w="12px"
                                h="12px"
                                mr={4}
                                bg={item.color || "#ffffff"}
                                display="inline-block"
                                borderRadius={"full"}
                            ></Box>
                            {item.code} - {item.year}
                        </Text>
                    ))}
                </SimpleGrid>
            </>
        );
    };

    const DetailsButton = ({
        rowKeyValue,
        isDetailsRowShown,
    }: ICellTextProps) => {
        return (
            <Button
                onClick={() => {
                    isDetailsRowShown
                        ? table.hideDetailsRow(rowKeyValue)
                        : table.showDetailsRow(rowKeyValue);
                }}
                variant={"unstyled"}
                size={"xs"}
            >
                <AppIcon
                    name={
                        isDetailsRowShown
                            ? "icl-chevron-bottom-alt"
                            : "icl-chevron-right-alt"
                    }
                    w={"1rem"}
                />
            </Button>
        );
    };

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "name":
                            return (
                                <Text
                                    textStyle={"mediumSm"}
                                    color={"var(--chakra-colors-black)"}
                                >
                                    {props?.rowData?.name}
                                </Text>
                            );
                        case "action":
                            return (
                                <HStack justify={"flex-end"} gap={3}>
                                    <AppOptionsBox
                                        options={actionOptions}
                                        data={props?.rowData}
                                        onClick={onActionClick}
                                    />
                                </HStack>
                            );
                        case "show-hide-details-row":
                            return <DetailsButton {...props} />;
                    }
                },
            },
            detailsRow: {
                elementAttributes: () => ({
                    style: {
                        backgroundColor: "var(--chakra-colors-primaryT95)",
                    },
                }),
                content: DetailsRow,
            },
        },
    };

    return <KaTable {...kaTableProps} />;
};
